import { useEffect, useRef, useState, useCallback } from "react";
import { X, Send } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_KEY = "mta_chat_session";

const STRINGS = {
  pt: {
    title: "Mãe e Rainha",
    subtitle: "Assistente do CIEES Portugal",
    disclaimer: "Assistente virtual sob o olhar da Mãe Três Vezes Admirável.",
    welcome: "Bem-vindo! Estou ao serviço da Mãe e Rainha Três Vezes Admirável. Posso ajudar com o CIEES, Schoenstatt, autoeducação, quotas, donativos, eventos ou com a sua vida espiritual. Em que posso ajudar?",
    placeholder: "Escreva a sua mensagem…",
    error: "Não consegui responder agora. Tente novamente, por favor.",
    open: "Falar com o assistente",
  },
  en: {
    title: "Mother and Queen",
    subtitle: "CIEES Portugal Assistant",
    disclaimer: "Virtual assistant under the gaze of the Mother Thrice Admirable.",
    welcome: "Welcome! I serve under the Mother and Queen Thrice Admirable. I can help with CIEES, Schoenstatt, self-education, membership fees, donations, events or your spiritual life. How may I help you?",
    placeholder: "Type your message…",
    error: "I could not answer right now. Please try again.",
    open: "Talk to the assistant",
  },
  es: {
    title: "Madre y Reina",
    subtitle: "Asistente del CIEES Portugal",
    disclaimer: "Asistente virtual bajo la mirada de la Madre Tres Veces Admirable.",
    welcome: "¡Bienvenido! Estoy al servicio de la Madre y Reina Tres Veces Admirable. Puedo ayudar con el CIEES, Schoenstatt, autoeducación, cuotas, donativos, eventos o con su vida espiritual. ¿En qué puedo ayudar?",
    placeholder: "Escriba su mensaje…",
    error: "No pude responder ahora. Inténtelo de nuevo, por favor.",
    open: "Hablar con el asistente",
  },
};

function getSessionId() {
  let id = window.localStorage.getItem(SESSION_KEY);
  if (!id) {
    id = (window.crypto?.randomUUID?.() || `mta-${Date.now()}-${Math.random().toString(36).slice(2)}`);
    window.localStorage.setItem(SESSION_KEY, id);
  }
  return id;
}

export const MTAChat = () => {
  const { lang } = useLang();
  const s = STRINGS[lang] || STRINGS.pt;
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [greeting, setGreeting] = useState("");
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const loadHistory = useCallback(async () => {
    try {
      const res = await fetch(`${API}/mta/chat/${getSessionId()}`);
      if (res.ok) {
        const data = await res.json();
        setMessages((data.messages || []).map((m) => ({ role: m.role, content: m.content })));
      }
    } catch (e) {
      console.warn("MTA chat history failed", e);
    }
    setLoaded(true);
  }, []);

  useEffect(() => {
    if (open && !loaded) loadHistory();
  }, [open, loaded, loadHistory]);

  useEffect(() => {
    if (!open || greeting) return;
    fetch(`${API}/mta/greeting?lang=${lang}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d?.text) setGreeting(d.text); })
      .catch((e) => console.warn("MTA greeting failed", e));
  }, [open, greeting, lang]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setBusy(true);
    setMessages((prev) => [...prev, { role: "user", content: text }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API}/mta/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: getSessionId(), message: text, lang }),
      });
      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let failed = false;
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          let payload;
          try { payload = JSON.parse(line.slice(6)); } catch { continue; }
          if (payload.delta) {
            setMessages((prev) => {
              const next = [...prev];
              next[next.length - 1] = { role: "assistant", content: next[next.length - 1].content + payload.delta };
              return next;
            });
          } else if (payload.error) {
            failed = true;
          }
        }
      }
      if (failed) throw new Error("stream_failed");
    } catch (e) {
      console.error("MTA chat failed", e);
      setMessages((prev) => {
        const next = [...prev];
        if (!next[next.length - 1].content) next[next.length - 1] = { role: "assistant", content: s.error };
        return next;
      });
    }
    setBusy(false);
  };

  return (
    <>
      {!open && (
        <button
          data-testid="mta-chat-button"
          onClick={() => setOpen(true)}
          aria-label={s.open}
          title={s.open}
          className="fixed bottom-5 right-5 z-50 h-16 w-16 rounded-full border-2 border-[#D4AF37] shadow-lg overflow-hidden bg-[#0E4D45] transition-transform hover:scale-105 focus:outline-none"
        >
          <img src="/mta-avatar.jpg" alt="MTA" className="h-full w-full object-cover" />
        </button>
      )}

      {open && (
        <div
          data-testid="mta-chat-panel"
          className="fixed bottom-5 right-5 z-50 flex flex-col w-[92vw] max-w-[380px] h-[70vh] max-h-[560px] bg-[#FDFBF7] border border-[#E5E0D8] shadow-2xl rounded-xl overflow-hidden"
          style={{ fontFamily: "Outfit, sans-serif" }}
        >
          <div className="flex items-center gap-3 bg-[#0E4D45] px-4 py-3">
            <img src="/mta-avatar.jpg" alt="MTA" className="h-11 w-11 rounded-full border-2 border-[#D4AF37] object-cover" />
            <div className="flex-1 min-w-0">
              <div className="text-white text-sm font-semibold leading-tight">{s.title}</div>
              <div className="text-[#D4AF37] text-[11px] leading-tight">{s.subtitle}</div>
            </div>
            <button data-testid="mta-chat-close" onClick={() => setOpen(false)} aria-label="Fechar" className="text-white/80 hover:text-white">
              <X size={20} />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-3">
            <div className="text-[10px] text-center text-[#4A5553] italic px-4">{s.disclaimer}</div>
            {messages.length === 0 && (
              <>
                {greeting && (
                  <div data-testid="mta-chat-greeting" className="bg-[#0E4D45]/5 border border-[#D4AF37]/50 rounded-lg px-3 py-2 text-sm italic text-[#0E4D45] leading-relaxed mr-8">
                    {greeting}
                  </div>
                )}
                <div data-testid="mta-chat-welcome" className="bg-white border border-[#E5E0D8] rounded-lg rounded-tl-none px-3 py-2 text-sm text-[#1A201F] leading-relaxed mr-8">
                  {s.welcome}
                </div>
              </>
            )}
            {messages.map((m, i) => (
              <div
                key={i}
                data-testid={m.role === "user" ? "mta-chat-message-user" : "mta-chat-message-assistant"}
                className={
                  m.role === "user"
                    ? "bg-[#0E4D45] text-white rounded-lg rounded-tr-none px-3 py-2 text-sm leading-relaxed ml-8 whitespace-pre-wrap"
                    : "bg-white border border-[#E5E0D8] rounded-lg rounded-tl-none px-3 py-2 text-sm text-[#1A201F] leading-relaxed mr-8 whitespace-pre-wrap"
                }
              >
                {m.content || (busy && i === messages.length - 1 ? "…" : "")}
              </div>
            ))}
          </div>

          <div className="flex items-center gap-2 border-t border-[#E5E0D8] bg-white px-3 py-2">
            <input
              data-testid="mta-chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder={s.placeholder}
              maxLength={2000}
              className="flex-1 text-sm px-2 py-2 outline-none bg-transparent text-[#1A201F] placeholder:text-[#9AA3A1]"
            />
            <button
              data-testid="mta-chat-send"
              onClick={send}
              disabled={busy || !input.trim()}
              aria-label="Enviar"
              className="h-9 w-9 flex items-center justify-center rounded-full bg-[#0E4D45] text-white disabled:opacity-40 hover:bg-[#0b3e38] transition-colors"
            >
              <Send size={16} />
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default MTAChat;
